/**
 * What appears in the sidebar and the mobile tab bar, grouped by role.
 *
 * A role's workspace is the ordered list of screens that role was built for —
 * the same pattern as kdoshr-app. A user with several roles gets the union of
 * their workspaces (`unionNavigationEntries`), deduplicated by view, which is
 * how a store operator who also holds `ticket_creator` lands both screens
 * without seeing someone else's.
 *
 * Every entry still checks its own visibility through `useCanSee`, which keeps
 * a link out when the view behind it is gated — the workspace is the grouping,
 * the view registry is the gate, and together they cover both axes.
 */
import { BookOpen, FileSpreadsheet, Home, PackageSearch, ScanLine, Warehouse } from 'lucide-react';
import type { ReactNode } from 'react';

import type { NavigableViewKey, ViewKey } from './views';

export interface NavigationEntry {
  view: NavigableViewKey;
  icon: ReactNode;
  /** Higher wins a place in the mobile tab bar, which fits three. */
  priority?: number;
}

const homeEntry: NavigationEntry = {
  view: 'home',
  icon: <Home className="size-4" aria-hidden />,
  priority: 100,
};
const destructionReceiptsEntry: NavigationEntry = {
  view: 'destructionReceipts',
  icon: <ScanLine className="size-4" aria-hidden />,
  priority: 75,
};
const unspscGuideEntry: NavigationEntry = {
  view: 'unspscGuide',
  icon: <BookOpen className="size-4" aria-hidden />,
  priority: 10,
};
const ticketCreatorWorkspace: NavigationEntry[] = [
  homeEntry,
  {
    view: 'productObservations',
    icon: <PackageSearch className="size-4" aria-hidden />,
    priority: 90,
  },
  unspscGuideEntry,
];

export const workspaces: Record<string, NavigationEntry[]> = {
  ticket_creator: ticketCreatorWorkspace,
  ticket_creator_site: ticketCreatorWorkspace,

  warehouse: [
    homeEntry,
    {
      view: 'warehouseProductObservations',
      icon: <Warehouse className="size-4" aria-hidden />,
      priority: 80,
    },
    destructionReceiptsEntry,
    unspscGuideEntry,
  ],
  management: [
    homeEntry,
    {
      view: 'productObservations',
      icon: <PackageSearch className="size-4" aria-hidden />,
      priority: 90,
    },
    {
      view: 'warehouseProductObservations',
      icon: <Warehouse className="size-4" aria-hidden />,
      priority: 80,
    },
    destructionReceiptsEntry,
    {
      view: 'sunatCommunicationBatches',
      icon: <FileSpreadsheet className="size-4" aria-hidden />,
      priority: 70,
    },
    unspscGuideEntry,
  ],
};

import type { CurrentUser } from '@/api/types';
/**
 * Merge the workspaces of every role the user holds, in role order, showing
 * each view once. A user without any known role still sees the home screen —
 * "Inicio" is the one entry everyone gets by default.
 */
export function navigationForRoleCodes(roleCodes: string[]): NavigationEntry[] {
  const seen = new Set<ViewKey>();
  return [...roleCodes.flatMap((code) => workspaces[code] ?? [])].filter((entry) => {
    if (seen.has(entry.view)) return false;
    seen.add(entry.view);
    return true;
  });
}

/**
 * Navigation for a specific user. A superuser is treated as holding every role
 * at once: hiding a screen from them would be a lie, since the API lets them
 * reach it all. A signed-in user without known roles still lands the home
 * screen.
 */
export function navigationForUser(
  user: Pick<CurrentUser, 'role_codes' | 'is_superuser'>,
): NavigationEntry[] {
  if (user.is_superuser) return navigationForRoleCodes(Object.keys(workspaces));
  return navigationForRoleCodes(user.role_codes);
}

/**
 * The mobile bar for a specific user. A superuser sees every view in
 * navigation at once, sorted by priority, and the bar slices to the top few —
 * hiding a screen from them would be a lie, since the API lets them reach it
 * all. Everyone else gets the union of their roles' workspaces.
 */
export function unionMobileNavigationForUser(
  user: Pick<CurrentUser, 'role_codes' | 'is_superuser'>,
): NavigationEntry[] {
  const roles = user.is_superuser ? Object.keys(workspaces) : user.role_codes;
  return [...navigationForRoleCodes(roles)].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}
